import { useTranslation } from 'react-i18next';

interface StatusBadgeProps {
  status:
    | 'active' | 'completed' | 'draft' | 'pending' | 'ongoing' | 'invited'
    | '進行中' | 'アクティブ' | '実施中' | '完了' | '準備中' | '未回答' | 'ドラフト';
}

export function StatusBadge({ status }: StatusBadgeProps) {
  const { t, i18n } = useTranslation();

  // Reference: px-2.5 py-0.5 text-[11px] font-bold rounded border
  const getStatusConfig = () => {
    switch (status) {
      // Japanese status keys (from reference code)
      case '進行中':
        return {
          styles: 'text-blue-700 bg-blue-50 border-blue-200',
          hasPulse: true,
          pulseColor: 'bg-blue-500',
        };
      case 'アクティブ':
        return {
          styles: 'text-emerald-700 bg-emerald-50 border-emerald-200',
          hasPulse: true,
          pulseColor: 'bg-emerald-500',
        };
      case '実施中':
        return {
          styles: 'text-blue-700 bg-blue-50 border-blue-200',
          hasPulse: true,
          pulseColor: 'bg-blue-500',
        };
      case '完了':
        return {
          styles: 'text-slate-600 bg-slate-100 border-slate-200',
          hasPulse: false,
          pulseColor: '',
        };
      case '準備中':
        return {
          styles: 'text-slate-500 bg-slate-50 border-slate-200',
          hasPulse: false,
          pulseColor: '',
        };
      case '未回答':
        return {
          styles: 'text-amber-700 bg-amber-50 border-amber-200',
          hasPulse: false,
          pulseColor: '',
        };
      case 'ドラフト':
        return {
          styles: 'text-slate-400 bg-white border-dashed border-slate-300',
          hasPulse: false,
          pulseColor: '',
        };
      // English status keys (legacy support)
      case 'active':
        return {
          styles: 'text-emerald-700 bg-emerald-50 border-emerald-200',
          hasPulse: true,
          pulseColor: 'bg-emerald-500',
        };
      case 'completed':
        return {
          styles: 'text-slate-600 bg-slate-100 border-slate-200',
          hasPulse: false,
          pulseColor: '',
        };
      case 'draft':
        return {
          styles: 'text-slate-400 bg-white border-dashed border-slate-300',
          hasPulse: false,
          pulseColor: '',
        };
      case 'pending':
      case 'invited':
        return {
          styles: 'text-amber-700 bg-amber-50 border-amber-200',
          hasPulse: false,
          pulseColor: '',
        };
      case 'ongoing':
        return {
          styles: 'text-blue-700 bg-blue-50 border-blue-200',
          hasPulse: true,
          pulseColor: 'bg-blue-500',
        };
      default:
        return {
          styles: 'text-slate-500 bg-slate-50 border-slate-200',
          hasPulse: false,
          pulseColor: '',
        };
    }
  };

  const getStatusLabel = () => {
    // If it's already a Japanese status key, return it directly
    if (['進行中', 'アクティブ', '実施中', '完了', '準備中', '未回答', 'ドラフト'].includes(status)) {
      // Return Japanese or English based on current language
      if (i18n.language === 'ja') {
        return status;
      }
      // Map Japanese to English
      const jaToEn: Record<string, string> = {
        '進行中': 'In Progress',
        'アクティブ': 'Active',
        '実施中': 'In Progress',
        '完了': 'Completed',
        '準備中': 'Preparing',
        '未回答': 'Pending',
        'ドラフト': 'Draft',
      };
      return jaToEn[status] || status;
    }

    // Use translation keys for English status keys
    switch (status) {
      case 'active':
        return t('dashboard.statusActive');
      case 'completed':
        return t('dashboard.statusCompleted');
      case 'draft':
        return t('dashboard.statusDraft');
      case 'pending':
        return t('dashboard.participantPending');
      case 'invited':
        return t('dashboard.participantPending'); // Show 'Pending' for invited too
      case 'ongoing':
        return t('dashboard.statusOngoing');
      default:
        return status;
    }
  };

  const config = getStatusConfig();

  return (
    <span
      className={`px-2.5 py-0.5 text-[11px] font-bold rounded border ${config.styles} whitespace-nowrap inline-flex items-center`}
    >
      {config.hasPulse && (
        <span className={`w-1.5 h-1.5 rounded-full ${config.pulseColor} mr-1.5 animate-pulse`} />
      )}
      {getStatusLabel()}
    </span>
  );
}
